// -----------------------------------------------------------------------------
// ProfileDetails.jsx – header badge inline + Meta rows + compact pipes list
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  Button,
  Stack,
} from "@mui/material";
import {
  LinkedIn as LinkedInIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import EditProfileForm from "./EditProfileForm";
import { getMedusaInspiredColor, getInitials } from "../../utils/utils";
import { getImageUrl } from "../../services/apiService";
import { useSelector } from "react-redux";

/* --------- helpers -------------------------------------------------------- */
const formatEnd = (exp) =>
  !exp.endDate || exp.endDate === "" || exp.current ? "Aujourd'hui" : exp.endDate;

const arr = (v) =>
  typeof v === "string" && v.trim() !== ""
    ? v.split(",").map((x) => x.trim())
    : Array.isArray(v)
    ? v
    : [];

/** Affiche les items en ligne “item | item | item” avec retour à la ligne au besoin */
const PipedList = ({ items, textSx }) => (
  <Box display="flex" flexWrap="wrap" alignItems="center">
    {items.map((itm, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && (
          <Typography sx={{ mx: 1, color: "#b0b0b0", fontWeight: 500 }}>|</Typography>
        )}
        <Typography sx={textSx}>{itm}</Typography>
      </React.Fragment>
    ))}
  </Box>
);

/* --------- composant principal ------------------------------------------- */
export default function ProfileDetails({ profile, onUpdated }) {
  const [imageUrl, setImageUrl] = useState("");
  const [editing, setEditing] = useState(false);
  const [showMoreSkills, setShowMoreSkills] = useState(false);
  const [showMoreForms, setShowMoreForms] = useState(false);
  const [showMoreCerts, setShowMoreCerts] = useState(false);
  const [showMoreExps, setShowMoreExps] = useState(false);
const currentUser = useSelector((state) => state.users.currentUser);
  useEffect(() => {
    (async () => {
      if (profile.localImagePath && profile.localImagePath !== "Non spécifié") {
        try {
          setImageUrl(await getImageUrl(profile.localImagePath));
        } catch {}
      }
    })();
  }, [profile.localImagePath]);

  /* --------- données ----------------------------------------------------- */
  const competences   = arr(profile.competences);
  const formations    = arr(profile.formations)
                        .filter((f) => f.toLowerCase() !== "non spécifié");
  const certifications = arr(profile.certifications)
                        .filter((c) => c.toLowerCase() !== "non spécifié");
  const experiences   = Array.isArray(profile.experiences) ? profile.experiences : [];
  /* Tarif display */

  const visSkills = showMoreSkills ? competences   : competences.slice(0, 4);
  const visForms  = showMoreForms  ? formations    : formations.slice(0, 3);
  const visCerts  = showMoreCerts  ? certifications: certifications.slice(0, 3);
  const visExps   = showMoreExps   ? experiences   : experiences.slice(0, 3);

  const badgeBg = "#e6f1ff";
  const badgeTx = "#007bff";

  const gridRow = (label, value) => (
    <>
      <Box
        display="grid"
        gridTemplateColumns="150px 1fr"
        alignItems="center"
        py={0.75}
      >
        <Typography sx={{ color: "#6c757d", fontWeight: 400, fontSize: "0.875rem" }}>
          {label}
        </Typography>
        <Typography sx={{ color: "#212529", fontWeight: 500, fontSize: "0.875rem" }}>
          {value}
        </Typography>
      </Box>
      <Divider />
    </>
  );

  /* --------- rendu ------------------------------------------------------- */
  return (
    <>
      <Box
        sx={{
          p: 4,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        {/* ------------ en‑tête (avatar + nom) ----------------------------- */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Tooltip title={profile.summary || ""} arrow>
              <Avatar
                src={imageUrl || undefined}
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: 32,
                  bgcolor:
                    !profile.localImagePath || profile.localImagePath === "Non spécifié"
                      ? getMedusaInspiredColor(profile.nom)
                      : "transparent",
                }}
              >
                {getInitials(profile.nom)}
              </Avatar>
            </Tooltip>
            <Box>
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="h5" fontWeight={700}>
                  {profile.nom}
                </Typography>
                <Chip
                  label={profile.disponibilite ? "Disponible" : "Non disponible"}
                  size="small"
                  sx={{
                    bgcolor: profile.disponibilite ? "#37d079" : "#ffe3e3",
                    color: profile.disponibilite ? "#fff" : "#a30000",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    borderRadius: "16px",
                  }}
                />
              </Box>
         
            </Box>
          </Box>

{currentUser.id === profile.userId && (
  <Button
    variant="contained"
    onClick={() => setEditing(true)}
    sx={{
      textTransform: "none",
      bgcolor: "#2F4E8B",
      color: "#fff",
      gap: 1,
      fontWeight: "bold",
    }}
  >
    <EditIcon fontSize="small" /> Modifier
  </Button>
)}

        </Box>

        <Divider />

        {/* ------------ méta‑données rapides ------------------------------- */}
        {gridRow("Poste", profile.poste)}
        {gridRow("Localisation", profile.localisation)}
        {gridRow("Expérience", `${profile.inter_exp} ans`)}
        {gridRow("Tarif", `${profile.tarif}/j`)}
        {profile.mode_travail  && profile.mode_travail  !== "Non spécifié" && gridRow("Mode de travail",  profile.mode_travail)}
        {profile.temps_travail && profile.temps_travail !== "Non spécifié" && gridRow("Temps de travail", profile.temps_travail)}

        {/* ------------ COMPÉTENCES (inchangées) --------------------------- */}
        <Box>
          <Box display="grid" gridTemplateColumns="150px 1fr" alignItems="flex-start" py={1}>
            <Typography sx={{ color: "#6c757d", fontWeight: 400, fontSize: "0.875rem" }}>
              Compétences
            </Typography>
            <Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {visSkills.map((s, i) => (
                  <Chip
                    key={i}
                    label={s}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      px: 1.5,
                      height: 28,
                      borderRadius: "16px",
                      bgcolor: i % 2 ? "#ece8ff" : badgeBg,
                      color: i % 2 ? "#6a4cff" : badgeTx,
                      boxShadow: "0 1px 2px rgba(0,0,0,.1)",
                    }}
                  />
                ))}
              </Box>
              {competences.length > 4 && (
                <Button
                  size="small"
                  onClick={() => setShowMoreSkills(!showMoreSkills)}
                  sx={{
                    mt: 0.75,
                    textTransform: "none",
                    bgcolor: badgeBg,
                    color: badgeTx,
                    fontWeight: 600,
                    borderRadius: "16px",
                    fontSize: 12,
                    px: 2,
                    py: 0.3,
                    "&:hover": { bgcolor: badgeBg, opacity: 0.85 },
                  }}
                >
                  {showMoreSkills
                    ? "Voir moins"
                    : `Voir plus (${competences.length - 4})`}
                </Button>
              )}
            </Box>
          </Box>
          <Divider />
        </Box>

        {/* ------------ FORMATIONS ---------------------------------------- */}
        {formations.length > 0 && (
          <>
            <Box display="grid" gridTemplateColumns="150px 1fr" alignItems="flex-start" py={1}>
              <Typography sx={{ color: "#6c757d", fontWeight: 400, fontSize: "0.875rem" }}>
                Formations
              </Typography>
              <Box>
                <PipedList
                  items={visForms}
                  textSx={{ color: "#212529", fontWeight: 400, fontSize: "0.875rem" }}
                />
                {formations.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => setShowMoreForms(!showMoreForms)}
                    sx={{
                      mt: 0.75,
                      textTransform: "none",
                      bgcolor: badgeBg,
                      color: badgeTx,
                      fontWeight: 600,
                      borderRadius: "16px",
                      fontSize: 12,
                      px: 2,
                      py: 0.3,
                      "&:hover": { bgcolor: badgeBg, opacity: 0.85 },
                    }}
                  >
                    {showMoreForms
                      ? "Voir moins"
                      : `Voir plus (${formations.length - 3})`}
                  </Button>
                )}
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {/* ------------ CERTIFICATIONS ------------------------------------ */}
        {certifications.length > 0 && (
          <>
            <Box display="grid" gridTemplateColumns="150px 1fr" alignItems="flex-start" py={1}>
              <Typography sx={{ color: "#6c757d", fontWeight: 400, fontSize: "0.875rem" }}>
                Certifications
              </Typography>
              <Box>
                <PipedList
                  items={visCerts}
                  textSx={{ color: "#212529", fontWeight: 400, fontSize: "0.875rem" }}
                />
                {certifications.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => setShowMoreCerts(!showMoreCerts)}
                    sx={{
                      mt: 0.75,
                      textTransform: "none",
                      bgcolor: badgeBg,
                      color: badgeTx,
                      fontWeight: 600,
                      borderRadius: "16px",
                      fontSize: 12,
                      px: 2,
                      py: 0.3,
                      "&:hover": { bgcolor: badgeBg, opacity: 0.85 },
                    }}
                  >
                    {showMoreCerts
                      ? "Voir moins"
                      : `Voir plus (${certifications.length - 3})`}
                  </Button>
                )}
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {/* ------------ EXPÉRIENCES (inchangées) -------------------------- */}
        {experiences.length > 0 && (
          <>
            <Box display="grid" gridTemplateColumns="150px 1fr" alignItems="flex-start" py={1}>
              <Typography sx={{ color: "#6c757d", fontWeight: 400, fontSize: "0.875rem" }}>
                Expériences
              </Typography>
              <Stack spacing={1} alignItems="flex-start">
                {visExps.map((exp, i) => (
                  <Box key={i} display="flex" gap={1.5} alignItems="flex-start">
                    <Avatar
                      src={exp.logoUrl || undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 16,
                        fontWeight: 700,
                        bgcolor: exp.logoUrl
                          ? "transparent"
                          : getMedusaInspiredColor(exp.company),
                      }}
                    >
                      {!exp.logoUrl && exp.company
                        ? exp.company.charAt(0).toUpperCase()
                        : "?"}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={700}>
                        {exp.title || exp.company}
                      </Typography>
                      {exp.title && (
                        <Typography variant="body2" color="text.secondary">
                          {exp.company}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {/* Affiche uniquement la plage de dates sans la durée */}
                        {exp.duration
                          ? exp.duration.replace(/\s*·.*$/, "")
                          : `${exp.startDate} – ${formatEnd(exp)}`}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {experiences.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => setShowMoreExps(!showMoreExps)}
                    sx={{
                      mt: 0.75,
                      textTransform: "none",
                      bgcolor: badgeBg,
                      color: badgeTx,
                      fontWeight: 600,
                      borderRadius: "16px",
                      fontSize: 12,
                      px: 2,
                      py: 0.3,
                      "&:hover": { bgcolor: badgeBg, opacity: 0.85 },
                    }}
                  >
                    {showMoreExps
                      ? "Voir moins"
                      : `Voir plus (${experiences.length - 3})`}
                  </Button>
                )}
              </Stack>
            </Box>
            <Divider />
          </>
        )}

        {/* ------------ RÉSEAUX SOCIAUX ------------------------------------ */}
        <Box display="grid" gridTemplateColumns="150px 1fr" alignItems="center" py={1}>
          <Typography sx={{ color: "#6c757d", fontWeight: 400, fontSize: "0.875rem" }}>
            Réseaux sociaux
          </Typography>
          <Box>
            {profile.linkedin_url && (
              <IconButton
                component="a"
                href={profile.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#2F4E8B",
                  "&:hover": { opacity: 0.8, background: "transparent" },
                }}
              >
                <LinkedInIcon fontSize="medium" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* ------------ formulaire édition ---------------------------------- */}
      {editing && (
        <EditProfileForm
          profile={profile}
          onClose={() => setEditing(false)}
          onUpdate={(updatedProfile) => {
            setEditing(false);
            if (typeof onUpdated === "function") onUpdated(updatedProfile || profile);
          }}
        />
      )}
    </>
  );
}
